import $ from 'jquery';
import Tooltip from '../../bootstrap/tooltip.js';
import Data from '../../bootstrap/dom/data.js';
import { defineJQueryPlugin, typeCheckConfig } from '../../util/plugin.js';
// import mpro from './mpro.init';

import { sanitizeHtml } from '../../bootstrap/util/sanitizer.js';
import { getOptionsFromDataset } from '../../util/dataset.js';
import { getElement } from '../../util/dom.js';

const AttachmentMap = {
	AUTO: 'auto',
	'AUTO-START': 'auto-start',
	'AUTO-END': 'auto-end',
	TOP: 'top',
	'TOP-START': 'top-start',
	'TOP-END': 'top-end',
	BOTTOM: 'bottom',
	'BOTTOM-START': 'bottom-start',
	'BOTTOM-END': 'bottom-end',
	RIGHT: 'right',
	'RIGHT-START': 'right-start',
	'RIGHT-END': 'right-end',
	LEFT: 'left',
	'LEFT-START': 'left-start',
	'LEFT-END': 'left-end',
};

const DISALLOWED_ATTRIBUTES = new Set(['sanitize', 'allowList', 'sanitizeFn']);

export class MpTooltip extends Tooltip {

	constructor(element, config) {
		super(element, config);
		// Attach the instance to the element
		// @ts-ignore
		this._element._tooltip = this;
	}

	_getConfig(config) {

		/** @type {any} */
		const ctr = this.constructor;

		const dataAttributes = getOptionsFromDataset(this._element, 'bs');

		for (const dataAttr of Object.keys(dataAttributes)) {
			if (DISALLOWED_ATTRIBUTES.has(dataAttr)) {
				delete dataAttributes[dataAttr];
			}
		}

		config = {
			...ctr.Default,
			...dataAttributes,
			...config,
		};

		config.container = config.container === false ? document.body : getElement(config.container);

		if (typeof config.delay === 'number') {
			config.delay = {
				show: config.delay,
				hide: config.delay,
			};
		}

		if (typeof config.title === 'number') {
			config.title = config.title.toString();
		}

		if (typeof config.content === 'number') {
			config.content = config.content.toString();
		}

		typeCheckConfig(ctr.NAME, config, ctr.DefaultType, ctr.DeprecatedConfig);

		if (config.sanitize) {
			config.template = sanitizeHtml(config.template, config.allowList, config.sanitizeFn);
		}

		return config;
	}

	_getPopperConfig(attachment) {

		/** @type {any} */
		const ctr = this.constructor;

		const defaultBsPopperConfig = {
			placement: attachment,
			modifiers: [
				{
					name: 'flip',
					options: {
						fallbackPlacements: this._config.fallbackPlacements,
					},
				},
				{
					name: 'offset',
					options: {
						offset: this._getOffset(),
					},
				},
				{
					name: 'preventOverflow',
					options: {
						boundary: this._config.boundary,
					},
				},
				{
					name: 'arrow',
					options: {
						element: `.${ctr.NAME}-arrow`,
						padding: 10, // this is why this method is extended
						// padding: ({ popper, reference, placement }) => {
						// 	return popper.width / reference.width;
						// },
					},
				},
				// ? Note: this modifier is ported from bs 5.3 but it doesn't seem to work here
				// {
				// 	name: 'preSetPlacement',
				// 	enabled: true,
				// 	phase: 'beforeMain',
				// 	fn: (data) => {
				// 		// Pre-set Popper's placement attribute in order to read the arrow sizes properly.
				// 		// Otherwise, Popper mixes up the width and height dimensions since the initial arrow style is for top placement
				// 		this.getTipElement().dataset.popperPlacement = data.state.placement;
				// 	},
				// },
				{
					name: 'onChange',
					enabled: true,
					phase: 'afterWrite',
					fn: data => this._handlePopperPlacementChange(data),
				},
			],
			onFirstUpdate: (data) => {
				if (data.options.placement !== data.placement) {
					this._handlePopperPlacementChange(data);
				}
			},
		};

		return {
			...defaultBsPopperConfig,
			...(typeof this._config.popperConfig === 'function' ? this._config.popperConfig(defaultBsPopperConfig) : this._config.popperConfig),
		};
	}

	_handlePopperPlacementChange(popperData) {
		const { state } = popperData;

		if (!state) {
			return;
		}

		this.tip = state.elements.popper;
		this._cleanTipClass();
		this.tip.dataset.popperPlacement = state.placement;
		this._addAttachmentClass(this._getAttachment(state.placement));
	}

	_getAttachment(placement) {
		return AttachmentMap[placement.toUpperCase()];
	}

	updateAttachment(attachment) {

		switch (attachment) {
			// case 'auto-start':
			// case 'auto-end':
			case 'top-start':
			case 'top-end': {
				attachment = 'top';
				break;
			}
			case 'bottom-start':
			case 'bottom-end': {
				attachment = 'bottom';
				break;
			}
			case 'left':
			case 'start': {
				attachment = 'start';
				break;
			}
			case 'right':
			case 'end': {
				attachment = 'end';
				break;
			}
		}

		return attachment;
	}

	dispose() {
		// Detach the instance from the element
		// @ts-ignore
		this._element._tooltip = null;
		super.dispose();
	}

	/**
	 * Return the Tooltip instance associated with the tooltip element
	 * ? Note: the tooltip element is the one that is created by the tooltip
	 *
	 * @param {HTMLElement} tooltipElement
	 */
	static getInstanceForTooltipElement(tooltipElement) {
		if (!tooltipElement) {
			return null;
		}

		const instance = Data.get(tooltipElement, MpTooltip.DATA_KEY);

		if (instance && instance instanceof Tooltip) {
			return instance;
		}

		return null;
	}

	/**
	 * Redefined the jQueryInterface to use our own class
	 *
	 * @param {*} config
	 *
	 * @this {JQuery}
	 */
	static jQueryInterface(config) {
		return this.each(function() {
			const data = MpTooltip.getOrCreateInstance(this, config);

			if (typeof config === 'string') {
				if (typeof data[config] === 'undefined') {
					throw new TypeError(`No method named "${config}"`);
				}

				data[config]();
			}
		});
	}
}

// TODO: add an init function for this (the container is NOT ready)
export const $container = $('div.tooltip-container');

// We cannot add both tooltip and popover delegates on the same container
export const bodyTooltip = new MpTooltip(document.body, {
	selector: '[data-bs-toggle="tooltip"]',
	container: $container[0] || document.body,
	// boundary: document.body,
	trigger: 'hover',

	/**
	 * @param {import('@popperjs/core').Options} defaultBsPopperConfig
	 */
	// popperConfig(defaultBsPopperConfig) {

	// 	let {
	// 		modifiers,
	// 	} = defaultBsPopperConfig;

	// 	if (!modifiers) {
	// 		modifiers = [];
	// 	}

	// 	modifiers.push(
	// 		{
	// 			name: 'flip',
	// 			options: {
	// 				flipVariations: true, // true by default
	// 				allowedAutoPlacements: ['top', 'bottom'],
	// 			},
	// 		},
	// 		// {
	// 		// 	name: 'arrow',
	// 		// 	options: {
	// 		// 		element: '.tooltip-arrow',
	// 		// 		padding: 10,
	// 		// 	},
	// 		// },
	// 		// {
	// 		// 	name: 'computeStyles',
	// 		// 	options: {
	// 		// 		gpuAcceleration: false, // true by default
	// 		// 	},
	// 		// },
	// 	);

	// 	defaultBsPopperConfig.modifiers = modifiers;

	// 	console.log('defaultBsPopperConfig', defaultBsPopperConfig);

	// 	return defaultBsPopperConfig;
	// },
});

/**
 * Remove all tooltips (optionally for a specific target)
 *
 * @param {HTMLElement=} targetElement
 */
mpro.removeTooltips = function(targetElement) {

	// Remove bs5 popovers
	// !Note: This loop is disabled because is very heavy
	// for (const element of $('[data-bs-toggle="tooltip"]', $container)) {
	// 	try {
	// 		// @ts-ignore
	// 		element._tooltip && element._tooltip.dispose();
	// 	}
	// 	catch (error) {
	// 		console.warn('Error while trying to dispose tooltip instance for element (%o): %o', element, error);
	// 	}
	// }

	// Remove all orphan tooltips
	// $('div[role="tooltip"]').remove();

	const $tipContainer = $container.length > 0 ? $container : $(document.body);
	const $tooltips = $tipContainer.find('div.tooltip[role="tooltip"]');
	let foundTarget = false;

	for (const element of $tooltips) {
		const instance = MpTooltip.getInstanceForTooltipElement(element);
		if (instance) {
			try {
				if (targetElement) {
					const target = instance._element;
					if (target === targetElement) {
						foundTarget = true;
					}
				}
				instance.dispose();

				if (foundTarget) {
					// We found the target element, so we can break the loop
					break;
				}
			}
			catch (error) {
				console.warn('Error while trying to dispose tooltip instance for element (%o): %o', element, error);
			}
		}
	}
};

// We also have to replace the original jQuery plugin interface
defineJQueryPlugin(MpTooltip);

export default mpro;
